import { getLogicEndpoint } from "./endpoints";
import { apiVersions } from "./endpoints";
import { isLocalMode, localFetch } from "./localRuntimeClient";

export type AppContext = {
  projectName: string;
  appName: string;
  getToken: () => Promise<string | null>;
};

export const getAppUrl = (opts: { projectName: string; appName: string }): string => {
  return `${getLogicEndpoint()}/automationProjects/${encodeURIComponent(opts.projectName)}/applications/${encodeURIComponent(opts.appName)}`;
};

export const getNewVfsUrl = (opts: { projectName: string; appName: string }): string => {
  return `${getAppUrl(opts)}/vfs`;
};

const isVfsUrl = (url: string): boolean => url.includes('/vfs');

/** Normalize a VFS URL to a cache key (strip query params) */
const vfsCacheKey = (url: string): string => url.split('?')[0] ?? url;
// Cache of ETags from VFS responses, keyed by URL path
const vfsETagCache = new Map<string, string>();

/**
 * Fetch the ETag for a VFS resource by doing a HEAD request.
 * Returns the ETag string or '*' if the resource doesn't exist (for new files).
 */
const getVfsETag = async (url: string, headers: Record<string, string>): Promise<string> => {
  // Check cache first
  const cached = vfsETagCache.get(vfsCacheKey(url));
  if (cached) {
    return cached;
  }

  try {
    const response = await fetch(url, {
      method: 'HEAD',
      headers,
      cache: 'no-store',
    });
    if (response.ok) {
      const etag = response.headers.get('etag') ?? '*';
      vfsETagCache.set(vfsCacheKey(url), etag);
      return etag;
    }
    return '*';
  } catch {
    return '*';
  }
};

/**
 * Fetch wrapper for VFS Api.
 *
 * - In local mode: no auth, routes to localhost
 * - In cloud mode: uses bearer token and full app URL
 * - For VFS PUT/DELETE without a caller If-Match: supplies an ETag and retries once on 412
 */
const vfsFetch = async <T>(
  url: string,
  options: RequestInit = {},
): Promise<T> => {
  // Normalize any HeadersInit form (plain object, `Headers` instance, or tuple
  // array, with arbitrary casing) into a concrete record. The `Headers` API
  // lowercases names, so the `If-Match` precondition below is detected regardless
  // of how the caller cased or shaped the header; canonical casing is then
  // restored on the outgoing record for the headers this layer reasons about.
  const CANONICAL_HEADER: Record<string, string> = {
    'content-type': 'Content-Type',
    'if-match': 'If-Match',
    authorization: 'Authorization',
  };
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  new Headers(options.headers as HeadersInit | undefined).forEach((value, key) => {
    headers[CANONICAL_HEADER[key] ?? key] = value;
  });

  // Supply an ETag only when the caller has not chosen its own concurrency
  // policy (draft writes intentionally use If-Match: * for overwrite semantics).
  const method = (options.method ?? 'GET').toUpperCase();
  const isWrite = method === 'PUT' || method === 'DELETE';
  const hasCallerIfMatch = Object.keys(headers).some(
    (name) => name.toLowerCase() === 'if-match',
  );
  const shouldManageEtag = isWrite && !hasCallerIfMatch;
  if (shouldManageEtag) {
    const etag = await getVfsETag(url, headers);
    headers['If-Match'] = etag;
  }

  const response = await fetch(url, {
    ...options,
    headers,
    cache: 'no-store',
  });

  if (!response.ok) {
    // Retry only when this client selected the ETag. A caller-supplied
    // precondition must not be silently replaced with a different one.
    if (response.status === 412 && shouldManageEtag) {
      vfsETagCache.delete(vfsCacheKey(url));
      delete headers['If-Match']; // remove stale before HEAD request
      const freshEtag = await getVfsETag(url, { ...headers });
      headers['If-Match'] = freshEtag;

      const retryResponse = await fetch(url, { ...options, headers, cache: 'no-store' });
      if (!retryResponse.ok) {
        const errorText = await retryResponse.text();
        throw new VfsFetchError(retryResponse.status, errorText);
      }

      // Cache new ETag from retry response
      const retryEtag = retryResponse.headers.get('etag');
      if (retryEtag) {
        vfsETagCache.set(vfsCacheKey(url), retryEtag);
      }

      const retryText = await retryResponse.text();
      if (!retryText) return {} as T;
      return JSON.parse(retryText);
    }

    // On any 412 (including caller-supplied If-Match), drop the cached ETag so a
    // subsequent default write does a fresh HEAD rather than reusing stale state.
    if (response.status === 412) {
      vfsETagCache.delete(vfsCacheKey(url));
    }
    const errorText = await response.text();
    throw new VfsFetchError(response.status, errorText);
  }

  // Cache ETag from successful VFS write responses for next call
  if (isWrite) {
    const newEtag = response.headers.get('etag');
    if (newEtag) {
      vfsETagCache.set(vfsCacheKey(url), newEtag);
    } else {
      // ETag changed, invalidate cache so next call does a fresh HEAD
      vfsETagCache.delete(vfsCacheKey(url));
    }
  }

  // Handle empty responses
  const text = await response.text();
  if (!text) {
    return {} as T;
  }

  return JSON.parse(text);
};

/**
 * Read a VFS JSON file and return both its parsed body and current ETag.
 *
 * WARNING: the returned `etag` is a GET/storage-namespace ETag (e.g. "0x8D…"). It
 * is intended for cache validation/diagnostics only and CANNOT be used as an
 * `If-Match` for a conditional write — the VFS PUT precondition is evaluated in a
 * different (runtime) namespace, so a GET ETag always fails it with a permanent
 * HTTP 412 (issue #2603). Conditional writes are not attempted for hosted-agent
 * cleanup; writes go through the default VFS write path (`If-Match: *`), which is
 * the strongest precondition this backend honors.
 *
 * Cloud only — callers must handle local mode separately. Mirrors `vfsFetch`
 * GET error semantics: a non-OK response throws `VfsFetchError` with the status
 * (so 404/403 branching in the strict readers keeps working).
 *
 * Security: this helper attaches a bearer token, so it must never be handed an
 * arbitrary URL. It builds the app's VFS URL itself from `AppContext` and accepts
 * only a bare file name (validated against path traversal / host injection), so
 * the credential can only ever reach the app's own VFS endpoint — no future
 * caller can turn it into a token-exfiltration primitive.
 */
// Require a `<name>.<ext>` shape so bare path segments ('.' and '..') are rejected:
// a permissive `[A-Za-z0-9._-]+` would admit '..', which URL-normalizes to the
// parent and could send the bearer token off the app's own `/vfs/` path.
const VFS_FILE_NAME = /^[A-Za-z0-9_-]+\.[A-Za-z0-9]+$/;

export const readVfsFileWithETag = async <T>(
  opts: AppContext,
  fileName: string,
): Promise<{ data: T; etag: string | null }> => {
  if (!VFS_FILE_NAME.test(fileName)) {
    throw new Error(`Invalid VFS file name: ${fileName}`);
  }
  const accessToken = await opts.getToken();
  if (!accessToken) {
    throw new Error('Failed to get access token for request');
  }
  const url = `${getNewVfsUrl(opts)}/${fileName}?api-version=${apiVersions.otto}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    cache: 'no-store',
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new VfsFetchError(response.status, errorText);
  }

  const etag = response.headers.get('etag');
  const text = await response.text();
  const data = (text ? JSON.parse(text) : {}) as T;
  return { data, etag };
};

/**
 * Typed error for VFS fetch failures with structured status and body.
 */
export class VfsFetchError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: string,
  ) {
    super(`VFS API request failed: ${status}`);
    this.name = 'VfsFetchError';
  }
}

/**
 * Fetch wrapper that supports both local and cloud modes.
 * In local mode, skips auth.
 * In cloud mode for ACA direct-plane calls, uses master key via acaFetch.
 * For ARM management calls, uses bearer token.
 */
export const appFetch = async <T>(url: string, accessToken: string, options?: RequestInit, defaultReturn?: T): Promise<T> => {
  if (isLocalMode()) {
    return localFetch<T>(url, {
      method: (options?.method as 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH') ?? 'GET',
      body: options?.body ? JSON.parse(options.body as string) : undefined,
      ...(options?.signal ? { signal: options.signal } : {}),
    });
  }

  const headers: Record<string, string> = {
    ...(options?.headers as Record<string, string> ?? {}),
    Authorization: `Bearer ${accessToken}`,
    'Content-Type': 'application/json',
  };

  if (isVfsUrl(url)) {
    try {
      // Normalize any HeadersInit form (e.g. a `Headers` instance carrying a
      // concrete `If-Match`) to a record first; a plain-object spread would
      // silently drop non-plain-object headers before vfsFetch sees them.
      const vfsHeaders = new Headers(options?.headers as HeadersInit | undefined);
      return await vfsFetch<T>(url, {
        ...options,
        headers: {
          ...Object.fromEntries(vfsHeaders),
          Authorization: `Bearer ${accessToken}`
        },
        cache: 'no-store'
      });
    } catch (error) {
      // Return default value for 404 errors (e.g., draft files that don't exist)
      if (defaultReturn !== undefined && error instanceof VfsFetchError && error.status === 404) {
        return defaultReturn;
      }
      throw error;
    }
  }

  // ARM calls use bearer token
  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new AppFetchError(response.status, errorText);
  }

  // Handle empty responses (e.g., 204 No Content, empty body)
  const text = await response.text();
  if (!text) {
    return (defaultReturn ?? {}) as T;
  }

  return JSON.parse(text);
};

/**
 * Typed error for VFS fetch failures with structured status and body.
 */
export class AppFetchError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: string,
  ) {
    // Status-only message (like VfsFetchError) so the response body isn't leaked
    // into user-facing notifications; callers that need it read `.body`.
    super(`Application API request failed: ${status}`);
    this.name = 'AppFetchError';
  }
}
